import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from '@kizen/kds/Button';
import ButtonWithDropdown from '__components/Button/WithDropdown';
import { getManageIntegrationSecrets } from 'store/authentication/selectors';
import { INTEGRATION_SECRETS_ROUTE } from '__pages/Settings/pages/ManageConnections/subroutes';
import { BUSINESS_APP_ACTIONS } from 'ts-components/Plugins/businessApps/constants';
import {
  type BusinessAppAction,
  type BusinessAppPermissions,
  getVersionActions,
  isAppDisabled,
} from 'ts-components/Plugins/businessApps/helpers';
import type { PendingBusinessAppAction } from 'ts-components/Plugins/businessApps/useBusinessAppActions';
import type {
  BusinessAppDetails,
  BusinessAppVersion,
} from 'ts-components/Plugins/businessApps/types';
import { kiCodeCopy } from 'ts-components/KiCode/copy';
import { useActionLabels } from '../Apps/columns';
import { CreateBusinessAppModal } from '../../components/CreateBusinessAppModal';

type DetailsActionsProps = {
  details: BusinessAppDetails;
  version?: BusinessAppVersion;
  permissions: BusinessAppPermissions;
  currentUserId?: string;
  onSelectAction: (request: PendingBusinessAppAction) => void;
};

const qaId = (action: BusinessAppAction) =>
  `business-app-details-${action.replace(/_/g, '-')}-button`;

export const DetailsActions: FC<DetailsActionsProps> = ({
  details,
  version,
  permissions,
  currentUserId,
  onSelectAction,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const secretsAccess = useSelector(getManageIntegrationSecrets);
  const labels = useActionLabels();
  const [showPrompt, setShowPrompt] = useState(false);

  if (!version) {
    return null;
  }

  const actions = getVersionActions(
    details,
    version,
    permissions,
    currentUserId,
    { includeView: false }
  );
  const has = (action: BusinessAppAction) => actions.includes(action);
  const select = (action: BusinessAppAction) =>
    onSelectAction({ action, app: details, version });
  const deleteAction = has(BUSINESS_APP_ACTIONS.deletePreview)
    ? BUSINESS_APP_ACTIONS.deletePreview
    : !details.is_preview &&
        permissions.canPublishBusinessApps &&
        isAppDisabled(details)
      ? BUSINESS_APP_ACTIONS.deleteApp
      : null;
  const toggleAction = [
    BUSINESS_APP_ACTIONS.disable,
    BUSINESS_APP_ACTIONS.enable,
  ].find(has);
  const editWithYourAILabel = t('Edit with Your AI');

  return (
    <>
      {deleteAction ? (
        <Button
          variant="text"
          color="warning"
          onClick={() => select(deleteAction)}
          qa={{ id: qaId(deleteAction) }}
        >
          {labels[deleteAction]}
        </Button>
      ) : null}
      {permissions.canPublishBusinessApps &&
      (secretsAccess.view || secretsAccess.edit) &&
      details.what_this_app_adds.secrets.length ? (
        <Button
          variant="text"
          color="primary"
          onClick={() => history.push(INTEGRATION_SECRETS_ROUTE)}
          qa={{ id: 'business-app-details-manage-secrets-button' }}
        >
          {t('Manage Secrets')}
        </Button>
      ) : null}
      {toggleAction ? (
        <Button
          color={
            toggleAction === BUSINESS_APP_ACTIONS.disable
              ? 'warning'
              : 'primary'
          }
          outline
          onClick={() => select(toggleAction)}
          qa={{ id: qaId(toggleAction) }}
        >
          {toggleAction === BUSINESS_APP_ACTIONS.disable
            ? t('Disable App')
            : t('Enable App')}
        </Button>
      ) : null}
      {has(BUSINESS_APP_ACTIONS.publishPreview) ? (
        <Button
          color="primary"
          outline
          onClick={() => select(BUSINESS_APP_ACTIONS.publishPreview)}
          qa={{ id: qaId(BUSINESS_APP_ACTIONS.publishPreview) }}
        >
          {labels[BUSINESS_APP_ACTIONS.publishPreview]}
        </Button>
      ) : null}
      {has(BUSINESS_APP_ACTIONS.editWithKiCode) ? (
        <ButtonWithDropdown
          noSpace
          color="green"
          onClick={() => select(BUSINESS_APP_ACTIONS.editWithKiCode)}
          onChange={() => setShowPrompt(true)}
          options={[{ value: 'your_ai', label: editWithYourAILabel }]}
          data-qa-id={qaId(BUSINESS_APP_ACTIONS.editWithKiCode)}
        >
          {kiCodeCopy(t).editWithKiCode}
        </ButtonWithDropdown>
      ) : permissions.canPublishBusinessApps ? (
        <Button
          color="primary"
          onClick={() => setShowPrompt(true)}
          qa={{ id: 'business-app-details-edit-with-your-ai-button' }}
        >
          {editWithYourAILabel}
        </Button>
      ) : null}
      {showPrompt ? (
        <CreateBusinessAppModal
          heading={t('Edit Business App')}
          onHide={() => setShowPrompt(false)}
        />
      ) : null}
    </>
  );
};
